import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation } from '../../trpc/trpc';
import { useShowToast } from '../../redux/slices/toasts/hooks';
import { trackEvent } from '../analytics/sml/analytics';
import { useApplyFreshUserToken } from '../team/useApplyFreshUserToken';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../settings/tabs/types';
import { useWorkspaces, WorkspaceSummary } from './useWorkspaces';
import { workspaceMessages } from './workspaceMessages';

export interface WorkspaceSwitchFailure {
  licenseId: number;
  /** tRPC FORBIDDEN: the membership row is gone (README decision 2). */
  lostMembership: boolean;
  /** The name of the chosen row. The alert keeps it after a refetch drops that row (a4). */
  workspaceName: string;
}

export interface SwitchWorkspaceState {
  /** The rows to show. While a switch runs, the list of the click: the token swap empties the cache. */
  rows: WorkspaceSummary[];
  /** The active row of `rows`. */
  active: WorkspaceSummary | null;
  /** Two or more rows, a switch that runs, or an alert (a2, a3, a4). */
  isVisible: boolean;
  /** `name` names the workspace in the toast when the caller knows it better than the list does. */
  switchTo: (licenseId: number, name?: string) => void;
  pendingLicenseId: number | null;
  failure: WorkspaceSwitchFailure | null;
  clearFailure: () => void;
}

interface SwitchVariables {
  licenseId: number;
  name: string;
  rows: WorkspaceSummary[];
}

function isForbidden(error: unknown): boolean {
  return (error as { data?: { code?: string } } | null)?.data?.code === 'FORBIDDEN';
}

/**
 * The page that opens after a switch. `subscription`: Settings > Subscription, for a member who
 * goes to the billing of their own workspace (design: workspaces f1).
 */
export type WorkspaceSwitchLanding = 'home' | 'subscription';

/**
 * `workspaces.switch`, then the token swap that resets every cache, then Home of the new workspace
 * and the toast "You are in {workspace}." (design: workspaces a3, b1). A refused switch reloads the
 * list (design: workspaces a4). The caller owns this state, so it can also dim its artist list (a3).
 */
export function useSwitchWorkspace(
  onSwitched?: () => void,
  landing: WorkspaceSwitchLanding = 'home'
): SwitchWorkspaceState {
  const intl = useIntl();
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const applyToken = useApplyFreshUserToken();
  const router = useSMLRouter();
  const queryClient = useQueryClient();
  const { showSuccess } = useShowToast();
  const { workspaces } = useWorkspaces();

  const mutation = useTRPCMutation({
    mutationFn: async ({ licenseId }: SwitchVariables) => {
      const { token } = await trpcClient.workspaces.switch.mutate({ licenseId });
      await applyToken(token);
    },
    onSuccess: (_data: void, { name }: SwitchVariables) => {
      trackEvent('workspace_switched');
      onSwitched?.();
      if (landing === 'subscription') {
        router.replace(SMLRoutes.settings, { tab: SMLSettingsTabId.subscription });
      } else {
        router.replace(SMLRoutes.home);
      }
      showSuccess(intl.formatMessage(workspaceMessages.switched, { workspace: name }));
    },
    onError: (error: unknown) => {
      if (isForbidden(error)) {
        queryClient.invalidateQueries({ queryKey: trpcAPI.workspaces.list.queryKey() }).catch(console.error);
      }
    },
  });

  const { mutate, reset, isPending, isError, error, variables } = mutation;
  const switchTo = useCallback(
    (licenseId: number, knownName?: string) => {
      const name = knownName ?? workspaces.find((workspace) => workspace.licenseId === licenseId)?.name ?? '';
      mutate({ licenseId, name, rows: workspaces });
    },
    [mutate, workspaces]
  );

  // A reset while the switch runs would hide its spinner; only a settled failure clears.
  const clearFailure = useCallback(() => {
    if (!isPending) {
      reset();
    }
  }, [isPending, reset]);
  const rows = isPending && variables !== undefined ? variables.rows : workspaces;
  const active = useMemo(() => rows.find((workspace) => workspace.isActive) ?? null, [rows]);
  const failure: WorkspaceSwitchFailure | null =
    isError && variables !== undefined
      ? { licenseId: variables.licenseId, lostMembership: isForbidden(error), workspaceName: variables.name }
      : null;
  return {
    rows,
    active,
    isVisible: rows.length > 1 || isPending || failure !== null,
    switchTo,
    pendingLicenseId: isPending ? (variables?.licenseId ?? null) : null,
    failure,
    clearFailure,
  };
}
